import { useRef, useEffect, useCallback, useMemo, useReducer } from "react";
import {
  isNumeric,
  getSelectedCells,
  isInSelection,
  normalizeSelection,
  useDataManagement,
  useShowToast,
  INITIAL_EDITOR_STATE,
  INITIAL_UI_STATE,
  editorReducer,
  uiReducer,
} from "./helper";
import CtxMenu from "./CtxMenu";
import { dbToGrid } from "./convert";
import Toolbar from "./Toolbar";
import { WORKBOOK_ROLES } from "../../../utils/shared";

const ExcelEditor = ({ spreadSheetData, setSpreadSheetData }) => {
  const { data, cols, dispatch, undo, redo, canUndo, canRedo } =
    useDataManagement();

  const [editor, dispatchEditor] = useReducer(
    editorReducer,
    INITIAL_EDITOR_STATE,
  );
  const [ui, dispatchUI] = useReducer(uiReducer, INITIAL_UI_STATE);

  const editorRef = useRef(null);
  const formulaRef = useRef(null);
  const colsRef = useRef(cols);

  useEffect(() => {
    colsRef.current = cols;
  }, [cols]);

  useEffect(() => {
    if (spreadSheetData?.rows) {
      const grid = dbToGrid(spreadSheetData.rows);

      dispatch({
        type: "SET_GRID",
        data: grid.data,
        colCount: grid.colCount,
      });
    }
  }, [spreadSheetData]);

  const showToast = useShowToast();
  const { selected, selection, ctxMenu, resizingCol, selecting } = ui;
  const numRows = data.length;
  const numCols = cols.length;

  const isViewer = useMemo(
    () => spreadSheetData?.role === WORKBOOK_ROLES.VIEWER,
    [spreadSheetData],
  );

  // ── Formula bar sync ──
  useEffect(() => {
    if (editor.cell) return;
    dispatchEditor({
      type: "SET_VALUE",
      value: data[selected.r]?.[selected.c] ?? "",
    });
  }, [selected, data, editor.cell]);

  // ── Editor ──
  const startEdit = useCallback(
    (r, c, initial) => {
      const value = initial !== undefined ? initial : (data[r]?.[c] ?? "");
      dispatchEditor({ type: "START_EDIT", r, c, value });
      setTimeout(() => editorRef.current?.focus(), 0);
    },
    [data],
  );

  // Fix: deps now include editor.cell.r/c so no stale closure
  const commitEdit = useCallback(
    (r, c, value) => {
      dispatch({ type: "SET_CELL", r, c, value });
      dispatchEditor({ type: "COMMIT" });
      dispatchEditor({ type: "SET_VALUE", value });
    },
    [dispatch],
  );

  const cancelEdit = useCallback(() => {
    dispatchEditor({ type: "CANCEL" });
    dispatchEditor({
      type: "SET_VALUE",
      value: data[selected.r]?.[selected.c] ?? "",
    });
  }, [data, selected]);

  // ── Keyboard ──
  const handleCellKeyDown = useCallback(
    (e, r, c) => {
      if (editor.cell) return;
      const { key, shiftKey, ctrlKey, metaKey } = e;
      const ctrl = ctrlKey || metaKey;

      if (key === "Enter" || key === "F2") {
        e.preventDefault();
        if (isViewer) return;
        startEdit(r, c);
        return;
      }

      if (key === "Delete" || key === "Backspace") {
        e.preventDefault();
        const norm = normalizeSelection(selection);
        if (norm) dispatch({ type: "CLEAR_RANGE", ...norm });
        else dispatch({ type: "SET_CELL", r, c, value: "" });
        dispatchEditor({ type: "SET_VALUE", value: "" });
        return;
      }

      if (ctrl && key === "c") {
        copySelection();
        return;
      }
      if (ctrl && key === "z") {
        undo();
        return;
      }
      if (ctrl && key === "y") {
        redo();
        return;
      }

      let nr = r,
        nc = c;
      if (key === "ArrowDown") nr = Math.min(numRows - 1, r + 1);
      else if (key === "ArrowUp") nr = Math.max(0, r - 1);
      else if (key === "ArrowRight") {
        e.preventDefault();
        nc = Math.min(numCols - 1, c + 1);
      } else if (key === "ArrowLeft") nc = Math.max(0, c - 1);
      else if (key === "Tab") {
        e.preventDefault();
        nc = shiftKey ? Math.max(0, c - 1) : Math.min(numCols - 1, c + 1);
      } else if (key.length === 1 && !ctrl) {
        if (isViewer) return;
        startEdit(r, c, key);
        return;
      }

      if (nr !== r || nc !== c) {
        e.preventDefault();
        dispatchUI({ type: "SELECT_CELL", r: nr, c: nc });
      }
    },
    [editor.cell, selection, numRows, numCols, startEdit, undo, redo, isViewer],
  );

  const handleEditorKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        commitEdit(editor.cell.r, editor.cell.c, editor.value);
        dispatchUI({
          type: "SELECT_CELL",
          r: Math.min(numRows - 1, editor.cell.r + 1),
          c: editor.cell.c,
        });
      } else if (e.key === "Tab") {
        e.preventDefault();
        commitEdit(editor.cell.r, editor.cell.c, editor.value);
        dispatchUI({
          type: "SELECT_CELL",
          r: editor.cell.r,
          c: Math.min(numCols - 1, editor.cell.c + 1),
        });
      } else if (e.key === "Escape") {
        cancelEdit();
      }
    },
    [editor, numRows, numCols, commitEdit, cancelEdit],
  );

  // ── Copy ──
  const copySelection = useCallback(() => {
    const norm = normalizeSelection(selection);
    let text;
    if (!norm) {
      text = data[selected.r]?.[selected.c] ?? "";
    } else {
      text = Array.from({ length: norm.r2 - norm.r1 + 1 }, (_, ri) =>
        Array.from(
          { length: norm.c2 - norm.c1 + 1 },
          (_, ci) => data[norm.r1 + ri]?.[norm.c1 + ci] ?? "",
        ).join("\t"),
      ).join("\n");
    }
    navigator.clipboard?.writeText(text);
    showToast("Copied!");
  }, [data, selected, selection, showToast]);

  // ── Grid ops (all go through dispatch) ──
  const addRow = useCallback(() => {
    dispatch({ type: "ADD_ROW" });
    showToast("Row added");
  }, [dispatch, showToast]);
  const addCol = useCallback(() => {
    dispatch({ type: "ADD_COL" });
    showToast("Column added");
  }, [dispatch, showToast]);
  const deleteRow = useCallback(
    (at) => {
      if (numRows <= 1) return;
      dispatch({ type: "DELETE_ROW", at });
      dispatchUI({
        type: "SELECT_CELL",
        r: Math.min(selected.r, numRows - 2),
        c: selected.c,
      });
      showToast("Row deleted");
    },
    [numRows, selected, dispatch, showToast],
  );
  const deleteCol = useCallback(
    (at) => {
      if (numCols <= 1) return;
      dispatch({ type: "DELETE_COL", at });
      dispatchUI({
        type: "SELECT_CELL",
        r: selected.r,
        c: Math.min(selected.c, numCols - 2),
      });
      showToast("Column deleted");
    },
    [numCols, selected, dispatch, showToast],
  );
  const insertRowAbove = useCallback(
    (at) => {
      dispatch({ type: "INSERT_ROW", at });
      showToast("Row inserted");
    },
    [dispatch, showToast],
  );
  const insertRowBelow = useCallback(
    (at) => {
      dispatch({ type: "INSERT_ROW", at: at + 1 });
      showToast("Row inserted");
    },
    [dispatch, showToast],
  );
  const insertColLeft = useCallback(
    (at) => {
      dispatch({ type: "INSERT_COL", at });
      showToast("Column inserted");
    },
    [dispatch, showToast],
  );
  const insertColRight = useCallback(
    (at) => {
      dispatch({ type: "INSERT_COL", at: at + 1 });
      showToast("Column inserted");
    },
    [dispatch, showToast],
  );
  const sortCol = useCallback(
    (c, asc) => {
      dispatch({ type: "SORT_COL", c, asc });
      showToast(`Sorted ${asc ? "A→Z" : "Z→A"}`);
    },
    [dispatch, showToast],
  );

  // ── Resize (uses ref — no stale cols) ──
  const startResize = useCallback(
    (e, colIdx) => {
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startW = colsRef.current[colIdx].width;
      dispatchUI({ type: "START_RESIZE", colIdx });

      const onMove = (ev) => {
        dispatch({
          type: "SET_COL_WIDTH",
          colIdx,
          width: Math.max(40, startW + ev.clientX - startX),
        });
      };
      const onUp = () => {
        dispatchUI({ type: "END_RESIZE" });
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [dispatch],
  ); // stable — no cols dep

  // ── Stats ──
  const stats = useMemo(() => {
    const cells = getSelectedCells(data, selected, selection);
    const nums = cells.filter((v) => isNumeric(v) && v !== "").map(Number);
    const norm = normalizeSelection(selection);
    const sum = nums.length ? nums.reduce((a, b) => a + b, 0) : null;
    return {
      count: norm ? (norm.r2 - norm.r1 + 1) * (norm.c2 - norm.c1 + 1) : 1,
      sum: sum != null ? sum : "-",
      avg: sum != null ? (sum / nums.length).toFixed(2) : "-",
    };
  }, [data, selected, selection]);

  const handleContextMenu = useCallback((e, r, c) => {
    e.preventDefault();
    dispatchUI({ type: "SELECT_CELL", r, c });
    dispatchUI({
      type: "SET_CONTEXT_MENU",
      menu: { x: e.clientX, y: e.clientY, r, c },
    });
  }, []);

  // ── CtxMenu handlers object (replaces 10 prop drills) ──
  const ctxHandlers = useMemo(
    () => ({
      startEdit,
      copySelection,
      insertRowAbove,
      insertRowBelow,
      insertColLeft,
      insertColRight,
      sortCol,
      deleteRow,
      deleteCol,
      close: () => dispatchUI({ type: "CLEAR_CONTEXT_MENU" }),
    }),
    [
      startEdit,
      copySelection,
      insertRowAbove,
      insertRowBelow,
      insertColLeft,
      insertColRight,
      sortCol,
      deleteRow,
      deleteCol,
    ],
  );

  const cellRef = `${cols[selected.c]?.key ?? ""}${selected.r + 1}`;

  return (
    <div
      className="xg-root"
      onClick={() => dispatchUI({ type: "CLEAR_CONTEXT_MENU" })}
      onMouseUp={() => dispatchUI({ type: "SET_SELECTING", selecting: false })}
    >
      {/* Toolbar */}
      <div className="xg-toolbar">
        <span className="xg-toolbar-title">
          📊 {spreadSheetData?.workbook_name}
        </span>
        <div className="xg-toolbar-sep" />
        <button className="xg-btn" onClick={addRow}>
          + Row
        </button>
        <button className="xg-btn" onClick={addCol}>
          + Col
        </button>
        <div className="xg-toolbar-sep" />
        <button className="xg-btn" onClick={() => sortCol(selected.c, true)}>
          ↑ A→Z
        </button>
        <button className="xg-btn" onClick={() => sortCol(selected.c, false)}>
          ↓ Z→A
        </button>
        <div className="xg-toolbar-sep" />
        <button className="xg-btn" onClick={undo} disabled={!canUndo}>
          ↩ Undo
        </button>
        <button className="xg-btn" onClick={redo} disabled={!canRedo}>
          ↪ Redo
        </button>
        <div className="xg-toolbar-sep" />
        <button className="xg-btn danger" onClick={() => deleteRow(selected.r)}>
          🗑 Del Row
        </button>
        <button className="xg-btn danger" onClick={() => deleteCol(selected.c)}>
          🗑 Del Col
        </button>
        <Toolbar
          spreadSheetData={spreadSheetData}
          setSpreadSheetData={setSpreadSheetData}
          data={data}
          copySelection={copySelection}
        />
      </div>

      {/* Formula bar */}
      <div className="xg-formula-bar">
        <span className="xg-cell-ref">{cellRef}</span>
        <span className="xg-formula-sep">fx</span>
        <input
          className="xg-formula-input"
          ref={formulaRef}
          value={editor.formula}
          onChange={(e) => {
            dispatchEditor({ type: "SET_VALUE", value: e.target.value });
            if (editor.cell) {
              dispatchEditor({ type: "SET_VALUE", value: e.target.value });
            } else {
              dispatch({
                type: "SET_CELL",
                r: selected.r,
                c: selected.c,
                value: e.target.value,
              });
            }
          }}
          disabled={isViewer}
          onFocus={() => {
            if (!editor.cell)
              startEdit(selected.r, selected.c, data[selected.r]?.[selected.c]);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              commitEdit(
                editor.cell?.r ?? selected.r,
                editor.cell?.c ?? selected.c,
                editor.formula,
              );
              formulaRef.current?.blur();
            }
            if (e.key === "Escape") {
              cancelEdit();
              formulaRef.current?.blur();
            }
          }}
        />
      </div>

      {/* Grid */}
      <div className="xg-grid-container">
        <table className="xg-table">
          <colgroup>
            <col style={{ width: 40 }} />
            {cols.map((col) => (
              <col key={col.key} style={{ width: col.width }} />
            ))}
            <col style={{ width: 32 }} />
          </colgroup>
          <thead>
            <tr className="xg-col-header-row">
              <th className="xg-row-num-header">#</th>
              {cols.map((col, ci) => (
                <th
                  key={col.key}
                  className={selected.c === ci ? "xg-selected-col" : ""}
                >
                  <div className="xg-col-header-inner">
                    <span>{col.key}</span>
                    <div
                      className={`xg-resize-handle${resizingCol === ci ? " active" : ""}`}
                      onMouseDown={(e) => startResize(e, ci)}
                    />
                  </div>
                </th>
              ))}
              <th className="xg-add-col-th" onClick={addCol} title="Add Column">
                +
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, ri) => (
              <tr key={ri}>
                <td
                  className={`xg-row-num${selected.r === ri ? " xg-selected-row" : ""}`}
                  onClick={() => {
                    dispatchUI({ type: "SELECT_CELL", r: ri, c: selected.c });
                    dispatchUI({
                      type: "SELECT_RANGE",
                      r1: ri,
                      c1: 0,
                      r2: ri,
                      c2: numCols - 1,
                    });
                  }}
                >
                  {ri + 1}
                </td>
                {row.map((val, ci) => {
                  const isSelected = selected.r === ri && selected.c === ci;
                  const isEditing =
                    editor.cell?.r === ri && editor.cell?.c === ci;
                  const isInSel = isInSelection(ri, ci, selection);

                  return (
                    <td
                      key={ci}
                      className={[
                        "xg-cell",
                        isSelected ? "xg-selected" : "",
                        isInSel && !isSelected ? "xg-in-selection" : "",
                        isEditing ? "xg-editing" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      tabIndex={0}
                      onClick={(e) => {
                        if (e.shiftKey) {
                          dispatchUI({
                            type: "SELECT_RANGE",
                            r1: selected.r,
                            c1: selected.c,
                            r2: ri,
                            c2: ci,
                          });
                        } else {
                          dispatchUI({ type: "SELECT_CELL", r: ri, c: ci });
                          if (!isEditing) dispatchEditor({ type: "CANCEL" });
                        }
                      }}
                      onDoubleClick={() => {
                        if (isViewer) return;
                        startEdit(ri, ci);
                      }}
                      onContextMenu={(e) => {
                        if (isViewer) return;
                        handleContextMenu(e, ri, ci);
                      }}
                      onKeyDown={(e) => handleCellKeyDown(e, ri, ci)}
                      onMouseDown={(e) => {
                        if (e.button === 0 && !e.shiftKey) {
                          dispatchUI({
                            type: "SET_SELECTING",
                            selecting: true,
                          });
                          dispatchUI({ type: "SELECT_CELL", r: ri, c: ci });
                          dispatchUI({
                            type: "SELECT_RANGE",
                            r1: ri,
                            c1: ci,
                            r2: ri,
                            c2: ci,
                          });
                        }
                      }}
                      onMouseEnter={() => {
                        if (selecting) {
                          dispatchUI({
                            type: "SELECT_RANGE",
                            r1: selection.r1,
                            c1: selection.c1,
                            r2: ri,
                            c2: ci,
                          });
                        }
                      }}
                    >
                      {isEditing ? (
                        <input
                          ref={editorRef}
                          className="xg-cell-editor"
                          value={editor.value}
                          onChange={(e) => {
                            dispatchEditor({
                              type: "SET_VALUE",
                              value: e.target.value,
                            });
                            e.target.style.width = "auto";
                            e.target.style.width = e.target.scrollWidth + "px";
                          }}
                          onKeyDown={handleEditorKeyDown}
                          onBlur={() => commitEdit(ri, ci, editor.value)}
                        />
                      ) : (
                        <div
                          className={`xg-cell-display${isNumeric(val) ? " xg-right" : ""}`}
                        >
                          {val}
                        </div>
                      )}
                    </td>
                  );
                })}
                <td style={{ background: "transparent", border: "none" }} />
              </tr>
            ))}
            <tr>
              <td
                className="xg-add-row-td"
                colSpan={numCols + 2}
                onClick={addRow}
                title="Add Row"
              >
                +
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Status bar */}
      <div className="xg-status-bar">
        <span className="xg-status-item">
          Cell: <span className="xg-status-val">{cellRef}</span>
        </span>
        <span className="xg-status-item">
          Count: <span className="xg-status-val">{stats.count}</span>
        </span>
        <span className="xg-status-item">
          Sum: <span className="xg-status-val">{stats.sum}</span>
        </span>
        <span className="xg-status-item">
          Avg: <span className="xg-status-val">{stats.avg}</span>
        </span>
        <span className="xg-status-item">
          <span className="xg-status-val">
            {numRows} rows × {numCols} cols
          </span>
        </span>
      </div>

      {ctxMenu && !isViewer && (
        <CtxMenu ctxMenu={ctxMenu} handlers={ctxHandlers} />
      )}
    </div>
  );
};

export default ExcelEditor;
